// Color math for theme generation. Pure functions, shared by the site, the
// asset generator, and the tests, so every published theme is verified by the
// same code that produced it.

export type Hex = `#${string}`

export function contrastRatio(foreground: string, background: string): number {
  const a = luminance(foreground)
  const b = luminance(background)
  return (
    Math.round(((Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05)) * 100) / 100
  )
}

export function luminance(hex: string): number {
  const [r, g, b] = toRgb(hex).map((channel) => {
    const value = channel / 255
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  })
  return r * 0.2126 + g * 0.7152 + b * 0.0722
}

export function toRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '')
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean.slice(0, 6)
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16)) as [
    number,
    number,
    number,
  ]
}

export function toHex([r, g, b]: [number, number, number]): Hex {
  return `#${[r, g, b]
    .map((v) =>
      Math.round(Math.min(255, Math.max(0, v)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}` as Hex
}

/** Linear blend of two colors; `amount` is the share of `b`. */
export function mix(a: string, b: string, amount: number): Hex {
  const x = toRgb(a)
  const y = toRgb(b)
  return toHex([0, 1, 2].map((i) => x[i] + (y[i] - x[i]) * amount) as [
    number,
    number,
    number,
  ])
}

/**
 * Moves `color` toward black or white in small steps until it reaches `ratio`
 * against `background`. Hue is kept because the blend only changes lightness
 * and saturation together, which keeps an agency color recognizable.
 */
export function ensureContrast(
  color: string,
  background: string,
  ratio: number,
  direction: 'darken' | 'lighten' = luminance(background) > 0.18
    ? 'darken'
    : 'lighten',
): Hex {
  const target = direction === 'darken' ? '#000000' : '#ffffff'
  for (let step = 0; step <= 100; step += 1) {
    const candidate = mix(color, target, step / 100)
    if (contrastRatio(candidate, background) >= ratio) return candidate
  }
  return target as Hex
}

/** Picks whichever of white or ink reads best on `background`. */
export function onColor(background: string, ink = '#000c1f'): Hex {
  return (
    contrastRatio('#ffffff', background) >= contrastRatio(ink, background)
      ? '#ffffff'
      : ink
  ) as Hex
}
